import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { alpha, useTheme } from '@mui/material/styles';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import HistoryIcon from '@mui/icons-material/History';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';

type Variant =
  | 'favorites'
  | 'recently-viewed'
  | 'no-results'
  | 'empty'
  | 'error';

interface AcademyEmptyStateProps {
  variant: Variant;
  searchQuery?: string;
  onRetry?: () => void;
  onClearSearch?: () => void;
}

const CONFIG: Record<
  Variant,
  {
    icon: React.ElementType;
    iconColor: 'warning.main' | 'primary.main' | 'error.main';
    title: string;
    body: string;
  }
> = {
  favorites: {
    icon: StarRoundedIcon,
    iconColor: 'warning.main',
    title: 'No favorites yet',
    body: 'Star your favorite tutorials to quickly access them here.',
  },
  'recently-viewed': {
    icon: HistoryIcon,
    iconColor: 'primary.main',
    title: 'No recently viewed courses',
    body: 'Any courses you open will appear here so you can quickly get back to them.',
  },
  'no-results': {
    icon: SearchOffIcon,
    iconColor: 'primary.main',
    title: '',
    body: "We couldn't find any courses for that term. Try a different search or clear the filter.",
  },
  empty: {
    icon: SchoolOutlinedIcon,
    iconColor: 'primary.main',
    title: 'No courses found',
    body: 'There are no courses here yet.',
  },
  error: {
    icon: ErrorOutlineIcon,
    iconColor: 'error.main',
    title: 'Oops! Something went wrong',
    body: 'We encountered an unexpected error while loading courses. Please try again later.',
  },
};

export function AcademyEmptyState({
  variant,
  searchQuery,
  onRetry,
  onClearSearch,
}: AcademyEmptyStateProps) {
  const theme = useTheme();
  const { icon: Icon, iconColor, title, body } = CONFIG[variant];

  let colorValue: string;
  if (iconColor === 'warning.main') colorValue = theme.palette.warning.main;
  else if (iconColor === 'error.main') colorValue = theme.palette.error.main;
  else colorValue = theme.palette.primary.main;

  const displayTitle =
    variant === 'no-results' && searchQuery
      ? `Nothing matched "${searchQuery}"`
      : title;

  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 10,
        px: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          bgcolor: alpha(colorValue, 0.12),
          color: iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1,
        }}
      >
        <Icon sx={{ fontSize: 40 }} />
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 600 }}>
        {displayTitle}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ maxWidth: 420, mx: 'auto' }}
      >
        {body}
      </Typography>
      {variant === 'error' && onRetry && (
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onRetry}
          sx={{ mt: 2, borderRadius: 999 }}
        >
          Try again
        </Button>
      )}
      {variant === 'no-results' && onClearSearch && (
        <Button
          variant="outlined"
          onClick={onClearSearch}
          sx={{ mt: 2, borderRadius: 999 }}
        >
          Clear search
        </Button>
      )}
    </Box>
  );
}
